/**
 * Module Studio: every module the reader may open, and the one they did.
 *
 * @module components/studio/StudioSurface
 */

import React, { useMemo } from "react";

import { Outlet, useNavigate, useParams } from "react-router";

import { programmeRoleFor } from "@/components/shared/programme-role";
import { ModuleList } from "@/components/studio/ModuleList";
import {
  Alert,
  Button,
  countLabel,
  EmptyState,
  MasterDetail,
  MutedText,
  useAsyncAction,
} from "@/components/ui";
import { useProgramme } from "@/hooks/useStore";
import { useEditorBase } from "@/router/programme-editor/SurfaceLayout";
import { useWorkspaceOutletContext } from "@/router/workspace-context";
import { editableModuleIds } from "@/state/store";
import type { Module } from "@/types";

/**
 * The modules the reader may open in the studio, in programme order. The list
 * and the studio's own index both read it, so neither opens a module the
 * other would not offer.
 */
export function useStudioModules(): Module[] {
  const { programme } = useProgramme();
  const { platformModules, session } = useWorkspaceOutletContext();
  const { programmeId } = useParams<{ programmeId?: string }>();
  const builder = programmeRoleFor(session, programmeId) === "builder";

  return useMemo<Module[]>(() => {
    // A builder opens only the modules they own; the server refuses the rest.
    const assigned = platformModules.filter(
      (entry) =>
        entry.programmeId === programmeId && (!builder || entry.ownerUserId === session.user.id),
    );
    const editable = new Set(
      builder ? assigned.map((entry) => entry.moduleId) : editableModuleIds(programme),
    );
    const fromStore = (programme.modules ?? []).filter((module) => editable.has(module.id));
    if (fromStore.length > 0) {
      return fromStore;
    }
    // The shell has the reader's assigned modules before the editor has read
    // the programme, so the list paints on a cold load rather than empty.
    return assigned.map(
      (entry) =>
        ({
          id: entry.moduleId,
          title: entry.moduleTitle,
          provisional: true,
        }) as unknown as Module,
    );
  }, [builder, platformModules, programme, programmeId, session]);
}

export function StudioSurface(): React.ReactElement {
  const { moduleId, tab, programmeId } = useParams<{
    moduleId?: string;
    tab?: string;
    programmeId?: string;
  }>();
  const base = `${useEditorBase()}/studio`;
  const navigate = useNavigate();
  const modules = useStudioModules();
  const { platformDataError, refreshPlatformWorkspace, session } = useWorkspaceOutletContext();
  const builder = programmeRoleFor(session, programmeId) === "builder";
  const retry = useAsyncAction(refreshPlatformWorkspace);

  if (modules.length === 0 && platformDataError) {
    return (
      <Alert tone="error">
        <span className="flex flex-wrap items-center gap-3">
          <span>Your modules could not be loaded.</span>
          <Button variant="ghost" size="sm" loading={retry.pending} onClick={() => retry.run()}>
            Try again
          </Button>
        </span>
      </Alert>
    );
  }

  if (modules.length === 0) {
    return (
      <EmptyState
        icon="puzzle-piece"
        title="No modules to edit"
        description={
          builder
            ? "Your programme lead assigns the modules you build."
            : "Add a module on the Modules section of the Programme Overview."
        }
      />
    );
  }

  // The list is already drawn, so a failed re-read has nowhere else to report
  // itself: what is on screen may be out of date and nothing would say so.
  const stale = platformDataError ? (
    <Alert tone="warning" className="mb-4" data-testid="studio-modules-stale">
      <span className="flex flex-wrap items-center gap-3">
        <span>This list may be out of date because it could not be refreshed.</span>
        <Button variant="ghost" size="sm" loading={retry.pending} onClick={() => retry.run()}>
          Try again
        </Button>
      </span>
    </Alert>
  ) : null;

  return (
    <>
      {stale}
      <MasterDetail
        label="Modules"
        selected={Boolean(moduleId)}
        onBack={() => navigate(base)}
        backLabel="All modules"
        master={
          <ModuleList
            modules={modules}
            activeModuleId={moduleId}
            base={base}
            tab={tab ?? "details"}
          />
        }
        masterFooter={<MutedText>{countLabel(modules.length, "module")}</MutedText>}
        detail={<Outlet />}
        data-testid="module-studio"
      />
    </>
  );
}

export default StudioSurface;
